import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Tab from '../partials/Tab';
import Header from '../auth/Header';
import QuestionText from './QuestionText';
import Question, {
  isCertificationQuestion,
  isSliderQuestion,
  isYesNoQuestion,
  nextStepIsFollowup,
  nextStepIsStop,
  nextStepIsContinue,
  STATUS_IN_PROGRESS,
  STATUS_COMPLETED,
  INSPECTION_BEFORE,
  INSPECTION_AFTER,
  PHOTOS_REQUIRED,
  ANSWER_MOMENT_FOLLOW_UP,
  QUESTION_TYPE_YES_NO
} from './Question';
import QuestionsProgress from './QuestionsProgress';
import StopInspectionModal from './StopInspectionModal';
import StartInspectionWorkModal from './StartInspectionWorkModal';
import GenerateReportModal from './GenerateReportModal';
import StopReportModal from './StopReportModal';
import { updateReportAnswer } from '../../actions/inspections';
import { setBeforeTab, setAfterTab, setQuestion, setQuestionAsync } from '../../actions/questions';
import __ from '../../helpers/translation';

import { useSwipeable } from 'react-swipeable';
import { useImageStore } from '../../lib/stores/image';
import { toast } from 'react-toastify';
import { useScheduledReportStore } from '../../lib/stores/scheduledReport';

export const Swipeable = ({children, ...props}) => {
  const handlers = useSwipeable(props);
  return (<div { ...handlers }>{children}</div>);
}

const propTypes = {
  inspection: PropTypes.object.isRequired,
  questions: PropTypes.object.isRequired,
  updateReportAnswer: PropTypes.func.isRequired,
  setBeforeTab: PropTypes.func.isRequired,
  setAfterTab: PropTypes.func.isRequired,
  setQuestion: PropTypes.func.isRequired,
  setQuestionAsync: PropTypes.func.isRequired,
};

class Questions extends Component {

  constructor (props) {
    super(props);

    this.onAnswer = this.onAnswer.bind(this);
    this.saveProgress = this.saveProgress.bind(this);
    this.startInspection = this.startInspection.bind(this);
    this.tabBeforeClick = this.tabBeforeClick.bind(this);
    this.tabAfterClick = this.tabAfterClick.bind(this);
    this.questionSelect = this.questionSelect.bind(this);
    
    this.saveProgressWithTimeout = null
    this.state = {
      leaveRight: false,
      leaveLeft: false,
      animate: false
    }

    //#region Retrieve and set the current answer

      const savedReport = useScheduledReportStore.getState().scheduledReports.find(report => report.data.id === this.props.inspection.report.id);
      if(!savedReport){
        console.log('[QUESTIONS_CONSTRUCTOR] savedReport not found');
        return;
      }
      console.log('[QUESTIONS_CONSTRUCTOR] savedReport', savedReport);

      /** @type {Array} */
      const answers = savedReport.data.answers || [];
      let lastAnsweredQuestion = null;
      for(let i = answers.length - 1; i >= 0; i--) {
        if(this.getAnswerCompletionStatus(answers[i]) === 'complete') {
          lastAnsweredQuestion = answers[i];
          break;
        }
      }

      if(lastAnsweredQuestion){
        this.props.setQuestion(lastAnsweredQuestion);
        if(lastAnsweredQuestion.moment !== INSPECTION_BEFORE) {
          this.props.setBeforeTab({ complete: true, active: false });
          this.props.setAfterTab({ disabled: false, active: true });
        }
      }
      else {
        this.props.setQuestion(savedReport.data.answers[0]);
      }

    //#endregion
  }

  //#region Refactored functions

    /**
     * This function gets the completion status of the answer.
     * 
     * @param {object} answer The answer object.
     * @returns {'missingImage' | 'missingAnswer' | 'complete'} The completion status of the answer.
     */
    getAnswerCompletionStatus(answer) {

      const images = Array.from(useImageStore.getState().images.values());

      if(answer.photos === PHOTOS_REQUIRED && !images.some(image => image.answerId === answer.id)){
        return 'missingImage';
      }

      if(!answer.answer){
        return 'missingAnswer';
      }

      return 'complete';
    }
  
    /**
     * Tests if all required questions are answered.
     * It considers whether follow-up questions should be answered
     * based on the answer to the parent question and the `yes_next_step` or `no_next_step` flags.
     * 
     * @returns {boolean} True if all required questions are answered, false otherwise.
     */
    isAllAnswered() {

      const { scheduledReports } = useScheduledReportStore.getState();
      const report = scheduledReports.find(report => report.data.id === this.props.inspection.report.id);

      if (!report) {
        return false;
      }

      const answers = report.data.answers || [];
      if (!answers.length) {
        return false;
      }

      // Get main answers (questions without a parent_question_id)
      const mainAnswers = answers.filter(answer => !answer.parent_question_id);

      for (const mainAnswer of mainAnswers) {
        // Check if the main answer is complete
        if (this.getAnswerCompletionStatus(mainAnswer) !== 'complete') {
          return false;
        }

        const answerValue = mainAnswer.answer;
        const yesNextStep = mainAnswer.yes_next_step;
        const noNextStep = mainAnswer.no_next_step;
        const isYesNoQuestion = mainAnswer.type === QUESTION_TYPE_YES_NO

        // Determine if a follow-up question is required based on the answer
        let followUpNeeded = false;
        const answerNumber = Number(answerValue);

        if(!isYesNoQuestion){ // Answered string question
          continue;
        }

        if (answerNumber === 1) { // Answered 'Yes'
          followUpNeeded = yesNextStep === ANSWER_MOMENT_FOLLOW_UP;
        } 
        else if (answerNumber === 0) { // Answered 'No'
          followUpNeeded = noNextStep === ANSWER_MOMENT_FOLLOW_UP;
        } 

        if (followUpNeeded) {
          // Find the follow-up question (sub-question)
          const subQuestion = answers.find(a => a.parent_question_id === mainAnswer.question_id);
          if (subQuestion) {
            // Check if the sub-question is complete
            if (this.getAnswerCompletionStatus(subQuestion) !== 'complete') {
              return false;
            }
          } else {
            // Sub-question expected but not found
            return false;
          }
        }
      }

      return true;
    }

    /**
     * Advances the inspection process to the next step based on the current state.
     *
     * This function determines the next question to display, handles transitions between
     * different parts of the inspection, and manages the display of modals for starting
     * the real inspection or generating a report. It ensures that all required questions
     * are answered before allowing the inspection to be completed.
     *
     * @returns {void}
     */
    nextStep() {

      const nextQuestion = this.getNextQuestion();

      // Handle scenario where the first part of the inspection is done
      if (
        nextQuestion &&
        nextQuestion.moment !== this.props.questions.currentQuestion.moment &&
        this.props.questions.tabAfter.disabled
      ) {
        window.$('#startRealInspection').modal('show');
        return;
      }

      // If there is a next question, navigate to it
      if (nextQuestion) {
        this.questionLeaveLeftEnterRight(() => this.props.setQuestionAsync(nextQuestion));

        if (this.isDifferentMomentFromCurrent(nextQuestion)) {
          this.props.setBeforeTab({ active: false });
          this.props.setAfterTab({ active: true });
        }
        return;
      }

      // If all questions are answered, show the generate report modal
      if (this.isAllAnswered()) {
        window.$('#generateReport').modal('show');
        return;
      }

      // Handle cases where not all questions are answered
      this.handleIncompleteQuestions();
    }

    /**
     * Handles the scenario where not all questions are answered.
     *
     * This function iterates through unanswered questions and displays appropriate
     * toast messages prompting the user to complete the required actions, such as
     * uploading photos or providing answers.
     *
     * @returns {void}
     */
    handleIncompleteQuestions() {

      const { scheduledReports } = useScheduledReportStore.getState();
      const report = scheduledReports.find(report => report.data.id === this.props.inspection.report.id);

      if (!report) {
        return false;
      }

      const answers = report.data.answers || [];
      if (!answers.length) {
        return false;
      }

      // Get main answers (questions without a parent_question_id)
      const mainAnswers = answers.filter(answer => !answer.parent_question_id);
      for (const mainAnswer of mainAnswers) {
        switch(this.getAnswerCompletionStatus(mainAnswer)){
          case 'missingImage': {
            toast.error(__('app.toasts.error_question_missing_images', { question: mainAnswer.question_data.question}));
            break;
          }
          case 'missingAnswer': {
            toast.error(__('app.toasts.error_question_incomplete', { question: mainAnswer.question_data.question}));
            break;
          }
          case 'complete': {
            
            const answerValue = mainAnswer.answer;
            const yesNextStep = mainAnswer.yes_next_step;
            const noNextStep = mainAnswer.no_next_step;
            const isYesNoQuestion = mainAnswer.type === QUESTION_TYPE_YES_NO

            // Determine if a follow-up question is required based on the answer
            let followUpNeeded = false;
            const answerNumber = Number(answerValue);

            if(!isYesNoQuestion){ // Answered string question
              break;
            }

            if (answerNumber === 1) { // Answered 'Yes'
              followUpNeeded = yesNextStep === ANSWER_MOMENT_FOLLOW_UP;
            }
            else if (answerNumber === 0) { // Answered 'No'
              followUpNeeded = noNextStep === ANSWER_MOMENT_FOLLOW_UP;
            }

            if(!followUpNeeded){
              break;
            }

            // Find the follow-up question (sub-question)
            const subQuestion = answers.find(a => a.parent_question_id === mainAnswer.question_id);
            if(!subQuestion){
              break;
            }

            switch(this.getAnswerCompletionStatus(subQuestion)){
              case 'missingImage': {
                toast.error(__('app.toasts.error_question_missing_images', mainAnswer.question_data.question));
                break;
              }
              case 'missingAnswer': {
                toast.error(__('app.toasts.error_question_incomplete', mainAnswer.question_data.question));
                break;
              }
              default: break;
            }
          
          }
        }
      }
    }

    /**
     * Retrieves the next question to display based on the current state.
     * Follow-up questions are handled based on the answer to the current question.
     * Questions are retrieved from the Zustand store, instead of the Redux store, for a
     * slow migration to Zustand in the entire application codebase.
     * 
     * @returns {object | null} The next question to display, or null if there is no next question found.
     */
    getNextQuestion(){

      // Check if the next question is a follow-up question
      const { scheduledReports } = useScheduledReportStore.getState();
      const report = scheduledReports.find(report => report.data.id === this.props.inspection.report.id);
      if (!report) {
        console.log('[GET_NEXT_QUESTION] report not found');
        return null;
      }

      /** @type {Array} */
      const answers = report.data.answers || [];
      const currentAnswer = answers.find(answer => answer.id === this.props.questions.currentQuestion.id);

      if(!currentAnswer){
        console.log('HIT getNextQuestion currentAnswer not found');
        return null;
      }

      console.log('[GET_NEXT_QUESTION] currentAnswer', currentAnswer);

      /** @type {number} The type of next step if answered yes */
      const yesNextStep = currentAnswer.yes_next_step; 
      /** @type {number} The type of next step if answered no */
      const noNextStep = currentAnswer.no_next_step;
      /** @type {boolean} True if the current question is a yes/no question */
      const isYesNoQuestion = currentAnswer.type === QUESTION_TYPE_YES_NO
      /** @type {number} The answer value as a number */
      const answerNumber = Number(currentAnswer.answer);

      // Answered string question, proceed to the next question
      if(!isYesNoQuestion){

        const nextAnswerIdx = answers.findIndex(answer => answer.id === currentAnswer.id) + 1;
        const nextAnswer = answers[nextAnswerIdx];

        if(nextAnswer?.parent_question_id === currentAnswer?.question_id){
          const newNextAnswer = answers[nextAnswerIdx + 1];
          console.log('[GET_NEXT_QUESTION] Answered string question, proceeding to (2): ', newNextAnswer);
          return newNextAnswer;
        }

        console.log('[GET_NEXT_QUESTION] Answered string question, proceeding to (1): ', nextAnswer);
        return nextAnswer || null;
      }

      // Follow-up question on answer YES, grab the subquestion
      else if (answerNumber === 1 && yesNextStep === ANSWER_MOMENT_FOLLOW_UP) { // Answered 'Yes' + follow-up
        const subQuestion = answers.find(a => a.parent_question_id === currentAnswer.question_id);
        console.log('[GET_NEXT_QUESTION] Answered YES + follow-up, grabbing subquestion: ', subQuestion);
        return subQuestion || null;
      }

      // Follow-up question on answer NO, grab the subquestion
      else if (answerNumber === 0 && noNextStep === ANSWER_MOMENT_FOLLOW_UP) { // Answered 'No'
        const subQuestion = answers.find(a => a.parent_question_id === currentAnswer.question_id);
        console.log('[GET_NEXT_QUESTION] Answered NO + follow-up, grabbing subquestion: ', subQuestion);
        return subQuestion || null; 
      }

      // If the next question is not a follow-up question, proceed to the next question
      else {

        const nextAnswerIdx = answers.findIndex(answer => answer.id === currentAnswer.id) + 1;
        const nextAnswer = answers[nextAnswerIdx];

        if(nextAnswer?.parent_question_id === currentAnswer?.question_id){
          const newNextAnswer = answers[nextAnswerIdx + 1];
          console.log('[GET_NEXT_QUESTION] Proceeding to new next question: ', newNextAnswer);
          return newNextAnswer;
        }

        console.log('[GET_NEXT_QUESTION] Proceeding to next question: ', nextAnswer);
        return nextAnswer || null;
      }
    }

  //#endregion

  onAnswer(value){
    let question = this.props.questions.currentQuestion;

    if(isCertificationQuestion(question)){
      if(this.isSubquestion()){
       this.completeParentAnswer();
      }

      if(nextStepIsStop(question, value)){
        this.saveProgress(value);
        window.$('#stopReasonModal').modal('show');
        return;
      }

      this.saveProgress(value, STATUS_COMPLETED);
      this.nextStep();
      return;
    }

    if(isSliderQuestion(question)){

      if(this.isSubquestion()){
        //if follow up then complete the parent question and child question
        this.completeParentAnswer();
      }
      this.saveProgress(value, STATUS_COMPLETED);
      this.nextStep();
      return;
    }

    if(isYesNoQuestion(question)){
      
      const isFollowup = nextStepIsFollowup(question, value);
      const isStop = nextStepIsStop(question, value);
      const isContinue = nextStepIsContinue(question, value);

      const { scheduledReports } = useScheduledReportStore.getState();
      const report = scheduledReports.find(report => report.data.id === this.props.inspection.report.id);
      const answers = report.data.answers || [];

      const currentAnswer = answers.find(answer => answer.id === this.props.questions.currentQuestion.id);

      console.log('HIT isYesNoQuestion', {
        currentQuestion: question,
        currentAnswer: currentAnswer,
        isFollowup,
        isStop,
        isContinue
      });


      if(nextStepIsFollowup(currentAnswer, value)){
        console.log('HIT nextStepIsFollowup');

        const { 
          updateReportAnswer: updateStoreReportAnswer,
          scheduledReports 
        } = useScheduledReportStore.getState();

        // Show Followup
        updateStoreReportAnswer(
          this.props.inspection.report.id, // reportId
          question.id, // answerId
          value, // answerValue
        )
        this.props.updateReportAnswer({ ... question, tempAnswer: value });
        
        // We need to retrieve the correct subquestion here to set in the Redux store
        const report = scheduledReports.find(report => report.data.id === this.props.inspection.report.id);
        const answers = report.data.answers || [];
        const subQuestion = answers.find(a => a.parent_question_id === question.question_id);

        console.log('HIT subQuestion', {
          zustand: subQuestion,
          redux: question.subquestion
        });

        this.props.setQuestion(subQuestion);
        
        return;
      }

      if(nextStepIsContinue(currentAnswer, value)){
        console.log('HIT nextStepIsContinue');
        
        if(this.isSubquestion()){
          console.log('HIT isSubquestion');
          this.completeParentAnswer();
        }

        this.saveProgress(value, STATUS_COMPLETED);
        this.nextStep();
        return;
      }

      if(nextStepIsStop(currentAnswer, value)){
        console.log('HIT nextStepIsStop');
        this.saveProgress(value);
        window.$('#stopReasonModal').modal('show');
        return;
      }

      console.log('HIT CONTINUE TO NEXT QUESTION');
      this.nextStep();
      return;
    }
  }

  isSubquestion(){
    return this.props.questions.currentQuestion.parent_question_id !== null;
  }

  isDifferentMomentFromCurrent(question){
    return question.moment != this.props.questions.currentQuestion.moment;
  }

  getParentQuestion(){
    return this.props.inspection.answers[this.getParentQuestionIndex()];
  }

  getParentQuestionIndex(){
    return this.props.inspection.answers.findIndex(answer => answer.question_id === this.props.questions.currentQuestion.parent_question_id);
  }

  getCurrentQuestionIndex(){
    return this.isSubquestion() ?
      this.getParentQuestionIndex() :
      this.props.inspection.answers.findIndex(answer => answer.id === this.props.questions.currentQuestion.id);
  }

  getPreviousQuestion(){

    if(this.isSubquestion()){
      return this.getParentQuestion();
    }

    if(!this.props.inspection.answers[this.getCurrentQuestionIndex() -1]){
      return null;
    }

    return this.props.inspection.answers[this.getCurrentQuestionIndex() - 1];
  }

  getLastQuestionFromMoment(moment){
    let momentQuestions = this.props.inspection.answers.filter(item => {
        return item.moment == moment;  
    });

    let lastCompleteIndex = -1;
    momentQuestions.map((question, index) => { 
      if(question.status){
        lastCompleteIndex = index;
      }
    });

    // If last completed is the last question
    if(lastCompleteIndex > -1 
      && (momentQuestions.length - 1) == lastCompleteIndex
    ){
      return momentQuestions[lastCompleteIndex];
    }

    // if last completed is not the last question, 
    // pick next quetion after completed one
    if(lastCompleteIndex > -1 ){
      return momentQuestions[lastCompleteIndex + 1];
    }

    // If no questions completed
    return momentQuestions[0];
  }

  previousStep(){
    let previousQuestion = this.getPreviousQuestion();

    if(previousQuestion){
      //this.props.setQuestionAsync(previousQuestion);
      this.questionLeaveRightEnterLeft(() => this.props.setQuestionAsync(previousQuestion))
      //this.props.setQuestion(previousQuestion)
      if(this.isDifferentMomentFromCurrent(previousQuestion)){
        this.props.setBeforeTab({ active: true });
        this.props.setAfterTab({ active: false });
      }
    }
  }

  questionLeaveRightEnterLeft(callback){
    this.questionLeaveRight().then(() => {
      this.questionEnterFromLeft().then(() => {
        callback().then(() => {
          setTimeout(() => {
            this.setState({ 
              leaveLeft: false,
              leaveRight: false,
              animate: true
            });  
          }, 100)
        })
      })
    });
  }

  questionLeaveLeftEnterRight(callback){
    this.questionLeaveLeft().then(() => {
      this.questionEnterFromRight().then(() => {
        callback().then(() => {
          setTimeout(() => {
            this.setState({ 
              leaveLeft: false,
              leaveRight: false,
              animate: true
            });  
          }, 100)
        })
      })
    });
  }

  questionEnterFromRight(){
    return new Promise(resolve => {
      this.setState({ 
          leaveLeft: false,
          leaveRight: true,
          animate: false
        }, () => {
          return resolve();
      });
    });
  }

  questionEnterFromLeft(){
    return new Promise(resolve => {
      this.setState({ 
          leaveLeft: true,
          leaveRight: false,
          animate: false
        }, () => {
          return resolve();
      });
    });
  }

  questionLeaveLeft(){
    return new Promise(resolve => {
      this.setState({ 
          leaveRight: false,
          leaveLeft: true,
          animate: true
        }, () => {
          setTimeout(resolve, 200); 
      });
    });
  }

  questionLeaveRight(){
    return new Promise(resolve => {
      this.setState({ 
          leaveRight: true,
          leaveLeft: false,
          animate: true
        }, () => {
          setTimeout(resolve, 200); 
      });
    });
  }

  onSwipeLeft(eventData){

    // Prevent swipe in input type range
    if(eventData.event.target.type && 
      eventData.event.target.type == 'range'
    ){
      return;
    }

    if(this.hasParentWithClass(eventData.event.target, 'modal')){
      return;
    }

    if(this.props.questions.currentQuestion.status != 1){
      return;
    }

    this.nextStep();
  }

  onSwipeRight(eventData){
    // Prevent swipe in input type range
    if(eventData.event.target.type && 
      eventData.event.target.type == 'range'
    ){
      return;
    }

    if(this.hasParentWithClass(eventData.event.target, 'modal')){
      return;
    }

    this.previousStep();
  }

  hasParentWithClass(element, classname) {
    if (element.className && element.className.split(' ').indexOf(classname)>=0) return true;
    return element.parentNode && this.hasParentWithClass(element.parentNode, classname);
  }

  isFullyAnswered(question){

    if(question.answer == null){
      return false;
    }

    if(question.photos && !question.images.length){
      return false;
    }

    if(nextStepIsStop(question, question.answer)){
      return false;
    }

    if(nextStepIsFollowup(question, question.answer) && question.subquestion){
      return this.isFullyAnswered(question.subquestion);
    }

    return true;
  }

  questionSelect(question){
    if(question.id == this.props.questions.currentQuestion.id){
      return;
    }
    this.props.setQuestion(question);
  }

  tabBeforeClick(){
    if(!this.props.questions.tabBefore.active){
      this.props.setBeforeTab({ active: true });
      this.props.setAfterTab({ active: false });
      this.props.setQuestion(this.getLastQuestionFromMoment(INSPECTION_BEFORE));
    }
  }

  tabAfterClick(){
    if(!this.props.questions.tabAfter.active && !this.props.questions.tabAfter.disabled){
      this.props.setBeforeTab({ active: false });
      this.props.setAfterTab({ active: true });
      // set last completed question as last question from tab
      this.props.setQuestion(this.getLastQuestionFromMoment(INSPECTION_AFTER));
    }
  }

  startInspection(){
    this.props.setBeforeTab({ complete: true, active: false });
    this.props.setAfterTab({ disabled: false, active: true });
    this.props.setQuestion(this.getNextQuestion());
    window.$('#startRealInspection').modal('hide');
  }

  saveProgress(answer, status = STATUS_IN_PROGRESS){

    let question = this.props.questions.currentQuestion;
    this.props.updateReportAnswer({
      ... question,
      answer: answer,
      status: status
    });

    const { 
      updateReportAnswer: updateStoreReportAnswer
    } = useScheduledReportStore.getState();

    updateStoreReportAnswer(
      this.props.inspection.report.id, // reportId
      question.id, // answerId
      answer, // answerValue
    )
  }

  completeParentAnswer(){
    //if follow up then complete the parent question and child question
    let parentQuestion = this.getParentQuestion();
    if(parentQuestion.tempAnswer){
      this.props.updateReportAnswer({ ... parentQuestion, answer: parentQuestion.tempAnswer });
    }
  }

  componentDidMount() {
    //this.setState({ leaveRight: true });
  }

  render () {

    if(!this.props.questions.currentQuestion){
      return '';
    }

    return (
      <Fragment>
        <Header 
          title={__('app.navigation.inspections')} 
          actions={this.isAllAnswered() ? 
            (
              <a 
                className="d-flex flex-row text-dark font-size-18 cur-p" 
                onClick={() => { 
                  window.$('#modal-navigation').modal('hide'); 
                  window.$('#generateReport').modal('show') 
                }} 
              >
                <div className="icon-container">
                  <i className="far fa-file-alt"></i>
                </div>
                <p>{ __('app.questions.menu_complete_inspection') }</p>
              </a>
            ) : (
              <a 
                className="d-flex flex-row text-dark font-size-18 cur-p" 
                onClick={() => { 
                  window.$('#modal-navigation').modal('hide'); 
                  window.$('#stopReport').modal('show') 
                }} 
              >
                <div className="icon-container">
                  <i className="far fa-file-alt"></i>
                </div>
                <p>{ __('app.questions.menu_stop_inspection') }</p>
              </a>
            )
          } 
        />
        <div className="container mt-sm-3">
          
          <div className="flex-row tab-bar d-flex justify-content-space-between">
            <Tab {... this.props.questions.tabBefore} callback={this.tabBeforeClick} />
            <Tab {... this.props.questions.tabAfter} callback={this.tabAfterClick} />
          </div>

          <Swipeable 
            onSwipedLeft={(eventData) => this.onSwipeLeft(eventData)} 
            onSwipedRight={(eventData) => this.onSwipeRight(eventData) } 
            preventDefaultTouchmoveEvent={false}
            trackMouse={true} 
          >
            <div 
              className={'row mt-3 swipe-container' +
                (this.state.animate ? ' sliding' : '') +
                (this.state.leaveRight ? ' slide-right' : '') + 
                (this.state.leaveLeft ? ' slide-left' : '')
              }
            >
                <QuestionText text={ this.props.questions.currentQuestion.question_data.question } />
                { this.props.questions.currentQuestion.parent_question_id ?
                  (
                  <div onClick={() => this.previousStep()}
                    className="col-md-2 my-auto text-center md-navigate d-md-block d-none">
                    <i className="fal fa-chevron-left"></i>
                  </div>
                  ) : ('')
                }
                <div className={`col-md-8 col-12 md-question ${this.props.questions.currentQuestion.parent_question_id ? '' : 'mx-auto'}`}>
                  <QuestionsProgress questionSelect={this.questionSelect} questions={this.props.inspection.answers} 
                    current={this.props.questions.currentQuestion} 
                    inspectionMoment={this.props.questions.currentQuestion.moment}  />
                  <Question 
                    current={this.props.questions.currentQuestion} 
                    removalType={this.props.inspection.report.removal}
                    onAnswer={(value) => this.onAnswer(value)} 
                    saveProgress={this.saveProgress} 
                  />
                </div>
            </div>
          </Swipeable>

          {/* Modals */}
          <StopInspectionModal current={this.props.questions.currentQuestion} />
          <StartInspectionWorkModal startInspection={this.startInspection} />
          <StopReportModal {...this.props} />
          <GenerateReportModal {...this.props} />

        </div>
      </Fragment>
    );
  }
}

Questions.propTypes = propTypes;

const mapStateToProps = ({inspection, questions}) => ({ inspection, questions });

const mapDispatchToProps = {
  updateReportAnswer,
  setBeforeTab,
  setAfterTab,
  setQuestion,
  setQuestionAsync
};

export default connect(mapStateToProps, mapDispatchToProps)(Questions);
